import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ExternalLink } from 'lucide-react';
import type { IntegrationProvider, IntegrationType } from '@/shared/types/nps';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/shared/ui/dialog';
import { SecretInput } from '@/shared/components/secret-input';
import { ProviderLogo } from '../../ui/provider-logo';
import { authShopify, createIntegration } from '../../api/use-nps';
import {
  wbuyIntegrationSchema,
  woocommerceIntegrationSchema,
  type WoocommerceIntegrationValues,
  type WbuyIntegrationValues,
} from '../../schemas/integration.schema';

const NUVEMSHOP_CLIENT = import.meta.env.VITE_NUVEMSHOP_CLIENT_ID ?? 'fixe-app';

interface Props {
  provider: IntegrationProvider | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConnected: () => void;
}

export function IntegrationConnectDialog({ provider, open, onOpenChange, onConnected }: Props) {
  if (!provider) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <ProviderLogo type={provider.type} size={44} />
            <DialogTitle>Conectar {provider.name}</DialogTitle>
          </div>
        </DialogHeader>
        <IntegrationAuthForm providerType={provider.type} onConnected={() => { onOpenChange(false); onConnected(); }} onCancel={() => onOpenChange(false)} />
      </DialogContent>
    </Dialog>
  );
}

function IntegrationAuthForm({
  providerType,
  onConnected,
  onCancel,
}: {
  providerType: IntegrationType;
  onConnected: () => void;
  onCancel: () => void;
}) {
  switch (providerType) {
    case 'NUVEMSHOP':
      return <NuvemshopAuth onCancel={onCancel} />;
    case 'WOOCOMMERCE':
      return <CredentialsForm type="WOOCOMMERCE" schema={woocommerceIntegrationSchema} onConnected={onConnected} onCancel={onCancel} />;
    case 'WBUY':
      return <CredentialsForm type="WBUY" schema={wbuyIntegrationSchema} onConnected={onConnected} onCancel={onCancel} />;
    case 'SHOPIFY':
      return <ShopifyAuth onConnected={onConnected} onCancel={onCancel} />;
    default:
      return null;
  }
}

function NuvemshopAuth({ onCancel }: { onCancel: () => void }) {
  const [params] = useSearchParams();
  const code = params.get('code');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!code) return;
    setBusy(true);
    createIntegration({ type: 'NUVEMSHOP', code })
      .then(() => {
        toast.success('Nuvemshop conectada.');
        window.history.replaceState({}, '', window.location.pathname);
      })
      .catch(() => toast.error('Falha ao autorizar Nuvemshop.'))
      .finally(() => setBusy(false));
  }, [code]);

  const url = `https://www.nuvemshop.com.br/apps/${NUVEMSHOP_CLIENT}/authorize`;

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Autorize o aplicativo Fixe CRM na sua loja Nuvemshop para concluir a integração.
      </p>
      <Button asChild loading={busy}>
        <a href={url} target="_blank" rel="noreferrer">
          <ExternalLink className="size-4" /> Autorizar aplicativo
        </a>
      </Button>
      <DialogFooter>
        <Button variant="secondary" onClick={onCancel}>Cancelar</Button>
      </DialogFooter>
    </div>
  );
}

function ShopifyAuth({ onConnected, onCancel }: { onConnected: () => void; onCancel: () => void }) {
  const [params] = useSearchParams();
  const [storeName, setStoreName] = useState('');
  const [busy, setBusy] = useState(false);

  const code = params.get('code');
  const state = params.get('state');
  const shop = params.get('shop');

  const finishOAuth = useCallback(async () => {
    if (!code || !state || !shop) return;
    setBusy(true);
    try {
      await createIntegration({ type: 'SHOPIFY', code, params: { state, store_url: shop } });
      toast.success('Shopify conectada.');
      window.history.replaceState({}, '', window.location.pathname);
      onConnected();
    } catch {
      toast.error('Falha ao conectar Shopify.');
    } finally {
      setBusy(false);
    }
  }, [code, state, shop, onConnected]);

  useEffect(() => {
    finishOAuth();
  }, [finishOAuth]);

  const startAuth = async () => {
    if (!storeName.trim()) return;
    setBusy(true);
    try {
      const { url } = await authShopify(storeName.trim());
      window.location.href = url;
    } catch {
      toast.error('Falha ao iniciar OAuth Shopify.');
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">Informe o nome da loja Shopify (subdomínio).</p>
      <div className="flex gap-2">
        <Input placeholder="minhaloja" value={storeName} onChange={(e) => setStoreName(e.target.value)} />
        <Button onClick={startAuth} loading={busy} disabled={!storeName.trim()}>Autorizar</Button>
      </div>
      <DialogFooter>
        <Button variant="secondary" onClick={onCancel}>Cancelar</Button>
      </DialogFooter>
    </div>
  );
}

function CredentialsForm({
  type,
  schema,
  onConnected,
  onCancel,
}: {
  type: 'WOOCOMMERCE' | 'WBUY';
  schema: typeof woocommerceIntegrationSchema;
  onConnected: () => void;
  onCancel: () => void;
}) {
  const form = useForm<WoocommerceIntegrationValues | WbuyIntegrationValues>({
    resolver: zodResolver(schema),
    defaultValues: { access_key: '', secret_key: '', params: { store_url: '' } },
  });

  const submit = form.handleSubmit(async (values) => {
    try {
      await createIntegration({ type, ...values });
      toast.success('Integração conectada.');
      onConnected();
    } catch {
      toast.error('Falha ao conectar.');
    }
  });

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="space-y-2">
        <Label>{type === 'WOOCOMMERCE' ? 'Consumer Key' : 'Usuário da API'}</Label>
        <Input {...form.register('access_key')} />
      </div>
      <div className="space-y-2">
        <Label>{type === 'WOOCOMMERCE' ? 'Consumer Secret' : 'Chave da API'}</Label>
        <SecretInput {...form.register('secret_key')} />
      </div>
      <div className="space-y-2">
        <Label>URL da loja</Label>
        <Input placeholder="https://minhaloja.com.br" {...form.register('params.store_url')} />
      </div>
      <DialogFooter>
        <Button type="button" variant="secondary" onClick={onCancel}>Cancelar</Button>
        <Button type="submit" loading={form.formState.isSubmitting}>Conectar</Button>
      </DialogFooter>
    </form>
  );
}
